import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import getCountryCodes from '../../../translations/countryCodes';

import { PrimaryButton } from '../../../components';

import css from './ShippingDestinationSection.module.css';

/**
 * Nextmine: lets the buyer pick their destination country, then shows the right
 * purchase path:
 *  - Destination === seller's country  → domestic flow (children = Buy now +
 *    Make an offer), with the seller's national shipping price shown up front.
 *  - Destination !== seller's country  → "Request international shipping". The
 *    buyer enters their postal code (so the seller can look up an accurate
 *    price), and cannot bid - they request a quote, then buy.
 *
 * The buyer's profile country is the default. If we don't know it, no purchase
 * option is shown until the buyer selects a destination - so nobody accidentally
 * buys at the national rate and then ships abroad.
 *
 * @component
 * @param {Object} props
 * @param {string} props.sellerCountryCode - The seller's country (ISO code)
 * @param {string} [props.sellerCountryName] - The seller's country name
 * @param {string} [props.buyerCountryCode] - The buyer's profile country (ISO code)
 * @param {string} [props.nationalShippingFormatted] - Formatted national shipping price
 * @param {boolean} props.isOwnListing
 * @param {boolean} props.sendShippingRequestInProgress
 * @param {propTypes.error} props.sendShippingRequestError
 * @param {Function} props.onSubmitShippingRequest - (destinationCode, destinationName, postalCode)
 * @param {React.ReactNode} props.children - the domestic Buy now + Make an offer flow
 * @returns {JSX.Element}
 */
const ShippingDestinationSection = props => {
  const intl = useIntl();
  const {
    sellerCountryCode,
    sellerCountryName,
    buyerCountryCode,
    nationalShippingFormatted,
    freeShippingNational,
    freeShippingInternational,
    isOwnListing,
    sendShippingRequestInProgress,
    sendShippingRequestError,
    onSubmitShippingRequest,
    children,
  } = props;

  const countries = getCountryCodes(intl.locale);
  const sellerName =
    sellerCountryName || countries.find(c => c.code === sellerCountryCode)?.name || sellerCountryCode;

  const [destination, setDestination] = useState(buyerCountryCode || '');
  const [postalCode, setPostalCode] = useState('');

  // Seller looking at their own listing → just show the domestic flow, no selector.
  if (isOwnListing) {
    return <>{children}</>;
  }

  // Nextmine: gratis fragt hele verden - ingen destinations-dropdown, ingen quote.
  // Alle købere (indenrigs + udland) får Buy now + Make an offer direkte.
  if (freeShippingInternational) {
    return (
      <div className={css.root}>
        <p className={css.freeShippingBanner}>
          <FormattedMessage id="ShippingDestinationSection.freeShippingWorldwide" />
        </p>
        <div className={css.childrenWrap}>{children}</div>
      </div>
    );
  }

  const isDomestic = destination && sellerCountryCode && destination === sellerCountryCode;
  const isInternational = destination && sellerCountryCode && destination !== sellerCountryCode;

  return (
    <div className={css.root}>
      <label className={css.label} htmlFor="shippingDestination">
        <FormattedMessage id="ShippingDestinationSection.label" />
      </label>
      <select
        id="shippingDestination"
        className={css.select}
        value={destination}
        onChange={e => setDestination(e.target.value)}
      >
        <option value="">{intl.formatMessage({ id: 'ShippingDestinationSection.placeholder' })}</option>
        {countries.map(c => (
          <option key={c.code} value={c.code}>
            {c.name}
          </option>
        ))}
      </select>

      {!destination ? (
        <p className={css.hint}>
          <FormattedMessage id="ShippingDestinationSection.selectPrompt" />
        </p>
      ) : isDomestic ? (
        <div className={css.domesticWrap}>
          {freeShippingNational ? (
            <p className={css.freeShippingBanner}>
              <FormattedMessage
                id="ShippingDestinationSection.domesticFreeShipping"
                values={{ country: sellerName }}
              />
            </p>
          ) : nationalShippingFormatted ? (
            <p className={css.shippingPrice}>
              <FormattedMessage
                id="ShippingDestinationSection.domesticShipping"
                values={{ country: sellerName, price: nationalShippingFormatted }}
              />
            </p>
          ) : null}
          <div className={css.childrenWrap}>{children}</div>
        </div>
      ) : isInternational ? (
        <div className={css.internationalWrap}>
          <p className={css.internationalNote}>
            <FormattedMessage
              id="ShippingDestinationSection.internationalNote"
              values={{ sellerCountry: sellerName }}
            />
          </p>
          <label className={css.label} htmlFor="shippingPostalCode">
            <FormattedMessage id="ShippingDestinationSection.postalCodeLabel" />
          </label>
          <input
            id="shippingPostalCode"
            type="text"
            className={css.postalInput}
            value={postalCode}
            onChange={e => setPostalCode(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'ShippingDestinationSection.postalCodePlaceholder',
            })}
          />
          {sendShippingRequestError ? (
            <p className={css.error}>
              <FormattedMessage id="ShippingDestinationSection.requestFailed" />
            </p>
          ) : null}
          <PrimaryButton
            type="button"
            inProgress={sendShippingRequestInProgress}
            disabled={!postalCode.trim()}
            onClick={() =>
              onSubmitShippingRequest(
                destination,
                countries.find(c => c.code === destination)?.name || destination,
                postalCode.trim()
              )
            }
          >
            <FormattedMessage id="ShippingDestinationSection.requestButton" />
          </PrimaryButton>
        </div>
      ) : (
        // No seller country known - fall back to the domestic flow so purchasing
        // is never fully blocked by missing seller data.
        <div className={css.domesticWrap}>
          <div className={css.childrenWrap}>{children}</div>
        </div>
      )}
    </div>
  );
};

export default ShippingDestinationSection;
